import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Eye,
  UserPlus,
  UserMinus,
  ArrowsClockwise,
  CheckCircle,
  XCircle,
  FilePdf,
  Note,
  Flag,
  Robot,
  Clock,
} from "@phosphor-icons/react";

// Meta table for each timeline event kind — icon, color, label builder.
const KIND_META = {
  created: {
    Icon: Plus, color: "#7CB2FF",
    label: (m) => `Ticket créé${m?.external_id ? ` · réf. ${m.external_id}` : ""}`,
  },
  webhook_updated: {
    Icon: ArrowsClockwise, color: "#94A3B8",
    label: () => "Ticket mis à jour depuis la source",
  },
  status_changed: {
    Icon: ArrowsClockwise, color: "#A78BFA",
    label: (m) => {
      const from = STATUS_LABEL[m?.from] || m?.from || "—";
      const to = STATUS_LABEL[m?.to] || m?.to || "—";
      const reason = m?.reason ? ` (${REASON_LABEL[m.reason] || m.reason})` : "";
      return `Statut : ${from} → ${to}${reason}`;
    },
  },
  assigned: {
    Icon: UserPlus, color: "#005CFF",
    label: (m) => `Affecté à ${m?.assigned_to_name || "un technicien"}`,
  },
  unassigned: {
    Icon: UserMinus, color: "#6B7280",
    label: () => "Désaffecté",
  },
  priority_changed: {
    Icon: Flag, color: "#F59E0B",
    label: (m) => {
      const from = PRIO_LABEL[m?.from] || m?.from || "—";
      const to = PRIO_LABEL[m?.to] || m?.to || "—";
      return `Priorité : ${from} → ${to}`;
    },
  },
  note_added: {
    Icon: Note, color: "#94A3B8",
    label: (m) => `Note interne ajoutée${m?.preview ? ` — « ${m.preview} »` : ""}`,
  },
  converted: {
    Icon: ArrowsClockwise, color: "#8B5CF6",
    label: (m) => `Converti en intervention${m?.intervention_title ? ` : ${m.intervention_title}` : ""}`,
  },
  auto_resolved: {
    Icon: Robot, color: "#10B981",
    label: (m) => `Résolution auto via intervention${m?.intervention_title ? ` « ${m.intervention_title} »` : ""}`,
  },
  report_generated: {
    Icon: FilePdf, color: "#10B981",
    label: (m) => {
      const n = (m?.recipients || []).length;
      const total = m?.attempted || n;
      if (!total) return "Rapport SAV généré";
      return `Rapport SAV généré · envoyé à ${n}/${total} destinataire${total > 1 ? "s" : ""}`;
    },
  },
};

const STATUS_LABEL = {
  open: "Ouvert", acknowledged: "Vu", in_progress: "En cours", resolved: "Résolu", closed: "Clos",
};
const PRIO_LABEL = {
  low: "Basse", normal: "Normale", high: "Haute", critical: "Critique",
};
const REASON_LABEL = {
  converted: "conversion",
  intervention_done: "intervention terminée",
};

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

/**
 * Compact vertical timeline for a SAV ticket.
 * Lazy-loads events on first mount; caller decides when to render.
 */
export default function SavTicketTimeline({ ticketId }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/sav-tickets/${ticketId}/timeline`);
        if (alive) setEvents(r.data || []);
      } catch (err) {
        if (alive) toast.error(formatApiError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [ticketId]);

  if (loading) {
    return (
      <div className="h-16 rounded-lg bg-white/[0.02] border border-white/5 animate-pulse" data-testid={`sav-timeline-loading-${ticketId}`} />
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="rounded-lg bg-white/[0.02] border border-dashed border-white/10 p-3 text-[11px] text-white/40 flex items-center gap-2" data-testid={`sav-timeline-empty-${ticketId}`}>
        <Clock size={12} weight="duotone" /> Aucun événement enregistré pour ce ticket
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3" data-testid={`sav-timeline-${ticketId}`}>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1.5">
        <Clock size={11} weight="duotone" /> Historique ({events.length})
      </div>
      <ol className="relative pl-4 space-y-2 before:content-[''] before:absolute before:left-[7px] before:top-1 before:bottom-1 before:w-px before:bg-white/10">
        {events.map((e) => {
          const meta = KIND_META[e.kind] || { Icon: Eye, color: "#94A3B8", label: () => e.kind };
          const { Icon, color } = meta;
          const line = meta.label(e.meta || {});
          return (
            <li key={e.id} className="relative" data-testid={`sav-timeline-event-${e.kind}`}>
              <span
                className="absolute -left-[13px] top-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center ring-2 ring-[#0a0a0a]"
                style={{ background: color + "33", color }}
              >
                <Icon size={9} weight="fill" />
              </span>
              <div className="text-[11.5px] leading-snug text-white/85">{line}</div>
              <div className="text-[9.5px] text-white/40 mt-0.5">
                {fmt(e.created_at)}
                {e.actor_name ? ` · ${e.actor_name}` : ""}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
